import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { mediaQuotes } from '../data/mock';

const MediaSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % mediaQuotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goTo = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goPrev = () => goTo((currentIndex - 1 + mediaQuotes.length) % mediaQuotes.length);
  const goNext = () => goTo((currentIndex + 1) % mediaQuotes.length);

  return (
    <section className="bg-gradient-to-b from-[#1a1a1b] to-[#0f0f10] py-20 sm:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-green-400 text-sm uppercase tracking-widest mb-4 block">Témoignages</span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ce que disent nos clients
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={goPrev}
            className="hidden md:flex absolute -left-16 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-all duration-300 hover:scale-110"
            aria-label="Témoignage précédent"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goNext}
            className="hidden md:flex absolute -right-16 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-all duration-300 hover:scale-110"
            aria-label="Témoignage suivant"
          >
            <ChevronRight size={24} />
          </button>

          {/* Testimonial Card */}
          <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-gray-700/50">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8 sm:left-12">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
                <Quote size={24} className="text-white" />
              </div>
            </div>

            {/* Content with animation */}
            <div className="pt-4">
              {mediaQuotes.map((quote, index) => (
                <div
                  key={quote.id}
                  className={`transition-all duration-500 ${
                    index === currentIndex
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-8 pointer-events-none'
                  }`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  {/* Quote Text */}
                  <p className="text-white text-xl sm:text-2xl leading-relaxed mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                    "{quote.quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                      {quote.author.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{quote.author}</p>
                      <p className="text-gray-400 text-sm">{quote.publication}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {mediaQuotes.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-8 h-3 bg-green-500'
                    : 'w-3 h-3 bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden justify-center gap-4 mt-6">
            <button
              onClick={goPrev}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goNext}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaSection;
